import React, {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactElement,
} from "react"
import type { ClassNameValue } from "tailwind-merge"

import { cn } from "@be-miniapp/ui/lib/utils"

import { useControlledState } from "../../hooks/use-controlled-state"
import { Button } from "./button"
import { ClearIcon } from "./icons/clear"

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactElement
  initialValue?: string
  isClearable?: boolean
  containerClassName?: ClassNameValue
  suffix?: ReactElement | null
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      icon,
      isClearable = true,
      initialValue = "",
      value,
      onChange: onValueChange = () => {},
      onFocus: onOutsideFocus,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false)

    const handleValueChange = useCallback(
      (value: string | undefined) => {
        onValueChange({
          target: { value: value ?? "" },
        } as ChangeEvent<HTMLInputElement>)
      },
      [onValueChange]
    )

    const [currentValue, setCurrentValue] = useControlledState<string>(
      value as string,
      initialValue ?? "",
      handleValueChange
    )

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value)
    }

    const clear = useCallback(() => {
      setCurrentValue("")
      ;(ref as React.RefObject<HTMLInputElement>)?.current?.focus()
      ;(ref as React.RefObject<HTMLInputElement>)?.current?.click()

      setIsFocused(true)
    }, [setCurrentValue, ref])

    const clearBtnRef = useRef<HTMLButtonElement>(null)

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onOutsideFocus) onOutsideFocus(e)
      setIsFocused(true)
    }

    const id = useId()

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.relatedTarget === clearBtnRef.current) return
      if (props.onBlur) props.onBlur(e)

      setTimeout(() => {
        setIsFocused(false)
      }, 100)
    }

    const isClearVisible = isClearable && isFocused && currentValue.length > 0

    return (
      <div
        className={cn("relative flex w-full items-center", containerClassName)}
      >
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 start-0 z-10 flex w-10 items-center justify-center">
            {icon}
          </div>
        )}

        <input
          id={id}
          type={type}
          ref={ref}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            "w-full bg-transparent py-2 text-sm",
            "file:border-0 file:bg-transparent file:font-medium file:text-sm",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
            icon ? "ps-10" : "ps-3",
            isClearVisible ? "pe-8" : "pe-3"
          )}
          value={value}
          {...props}
        />

        {isClearVisible && (
          <Button
            variant="ghost"
            type="button"
            tabIndex={-1}
            className="absolute inset-y-0 end-0 z-50 flex h-full shrink-0 items-center justify-center pe-3"
            onClick={clear}
            size="auto"
            ref={clearBtnRef as React.RefObject<HTMLButtonElement>}
          >
            <ClearIcon name="clear" className="w-[1.2rem]" />
          </Button>
        )}

        {props.suffix}
      </div>
    )
  }
)

export { Input }
